import { BaseJobHandler } from "@brains/plugins";
import { SITE_CHANNELS } from "@brains/contracts";
import type { ServicePluginContext } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import type { ProgressReporter } from "@brains/utils/progress";
import type { ISiteBuilder } from "../types/site-builder-types";
import type { LayoutComponent, LayoutSlots } from "@brains/site-engine";
import type { SiteBuilderConfig } from "../config";
import {
  siteBuildJobSchema,
  type SiteBuildJobData,
  type SiteBuildJobResult,
} from "../types/job-types";
import { EntityUrlGenerator } from "@brains/site-composition";
import { resolveSiteMetadata } from "../lib/site-metadata";
import type { SiteBuildStatusService } from "../lib/site-build-status";

/**
 * The four transitions this handler records. The service does more; asking for
 * all of it meant a test could not record a subset without asserting it was
 * the whole service.
 */
export type BuildStatusRecorder = Pick<
  SiteBuildStatusService,
  | "markBuilding"
  | "markSuccess"
  | "markSkipped"
  | "markCancelled"
  | "markFailure"
>;
import { getErrorMessage } from "@brains/utils/error";

export interface SiteBuildJobHandlerConfig {
  siteBuilder: ISiteBuilder;
  layouts: Record<string, LayoutComponent>;
  defaultSiteConfig: SiteBuilderConfig["siteInfo"];
  sharedImagesDir: string;
  siteUrl?: string | undefined;
  previewUrl?: string | undefined;
  /** Local runtime URL, such as http://localhost:8080. */
  localSiteUrl?: string | undefined;
  /** Prefer local URLs while the app is running outside deployed production. */
  preferLocalUrls?: boolean | undefined;
  themeCSS?: string | undefined;
  slots?: LayoutSlots | undefined;
  getHeadScripts?: (() => string[]) | undefined;
  /** Inline static assets supplied by the SitePackage (e.g. canvas scripts) */
  staticAssets?: Record<string, string> | undefined;
  statusService?: BuildStatusRecorder | undefined;
  onBuildStarted?:
    | ((
        environment: "preview" | "production",
        jobId: string,
        inputGeneration: number,
      ) => void | Promise<void>)
    | undefined;
  onBuildFinished?:
    | ((
        environment: "preview" | "production",
        jobId: string,
        inputGeneration: number,
      ) => void | Promise<void>)
    | undefined;
}

/**
 * Job handler for site building operations
 */
export class SiteBuildJobHandler extends BaseJobHandler<
  "site-build",
  SiteBuildJobData,
  SiteBuildJobResult
> {
  private sendMessage: ServicePluginContext["messaging"]["send"];
  private cfg: SiteBuildJobHandlerConfig;
  constructor(
    logger: Logger,
    sendMessage: ServicePluginContext["messaging"]["send"],
    cfg: SiteBuildJobHandlerConfig,
  ) {
    super(logger, {
      schema: siteBuildJobSchema,
      jobTypeName: "site-build",
    });
    this.sendMessage = sendMessage;
    this.cfg = cfg;
  }

  async process(
    data: SiteBuildJobData,
    jobId: string,
    progressReporter: ProgressReporter,
  ): Promise<SiteBuildJobResult> {
    // Apply defaults for optional fields
    const environment = data.environment ?? "preview";
    const enableContentGeneration = data.enableContentGeneration ?? false;
    const inputGeneration = data.inputGeneration ?? 0;

    await this.recordStatus(
      () => this.cfg.statusService?.markBuilding(environment, jobId),
      "building",
    );
    await this.recordLifecycle(
      () =>
        this.cfg.onBuildStarted?.(environment, jobId, inputGeneration) ??
        Promise.resolve(),
      "started",
    );

    try {
      this.logger.debug("Starting site build job", {
        jobId,
        environment,
        outputDir: data.outputDir,
      });

      // Report initial progress
      await progressReporter.report({
        progress: 0,
        total: 100,
        message: `Starting site build for ${environment} environment`,
      });

      // Create a sub-reporter that maps build progress to job progress
      const buildProgressReporter = progressReporter.createSub({
        scale: { start: 10, end: 90 },
      });

      const siteConfig = await resolveSiteMetadata(
        this.sendMessage,
        data.siteConfig ?? this.cfg.defaultSiteConfig,
      );
      const configuredSiteUrl =
        environment === "preview"
          ? (this.cfg.previewUrl ?? this.cfg.siteUrl)
          : this.cfg.siteUrl;
      const siteUrl = this.cfg.preferLocalUrls
        ? (this.cfg.localSiteUrl ?? configuredSiteUrl)
        : configuredSiteUrl;

      // Perform the build
      const result = await this.cfg.siteBuilder.build(
        {
          outputDir: data.outputDir,
          workingDir: data.workingDir,
          sharedImagesDir: this.cfg.sharedImagesDir,
          enableContentGeneration,
          environment,
          cleanBeforeBuild: true,
          siteConfig,
          siteUrl,
          layouts: this.cfg.layouts,
          themeCSS: this.cfg.themeCSS,
          slots: this.cfg.slots,
          headScripts: this.cfg.getHeadScripts?.(),
          ...(this.cfg.staticAssets && {
            staticAssets: this.cfg.staticAssets,
          }),
        },
        buildProgressReporter.toCallback(),
      );

      if (result.success && result.skipped) {
        await this.recordStatus(
          () =>
            this.cfg.statusService?.markSkipped(
              environment,
              jobId,
              result.routesBuilt,
            ),
          "skipped",
        );
      } else if (result.success) {
        await this.recordStatus(
          () =>
            this.cfg.statusService?.markSuccess(
              environment,
              jobId,
              result.routesBuilt,
              result.warnings ?? [],
            ),
          "success",
        );
      } else if (result.cancelled) {
        await this.recordStatus(
          () =>
            this.cfg.statusService?.markCancelled(
              environment,
              jobId,
              result.errors?.join("; ") ?? "Site build cancelled",
            ),
          "cancelled",
        );
      } else {
        await this.recordStatus(
          () =>
            this.cfg.statusService?.markFailure(
              environment,
              jobId,
              result.errors?.join("; ") ?? "Site build failed",
            ),
          "failure",
        );
      }

      // Report completion
      await progressReporter.report({
        progress: 100,
        total: 100,
        message: result.cancelled
          ? "Site build cancelled"
          : `Site build completed: ${result.routesBuilt} routes built`,
      });

      this.logger.debug("Site build job completed", {
        jobId,
        environment,
        routesBuilt: result.routesBuilt,
        success: result.success,
        cancelled: result.cancelled ?? false,
      });

      // A skipped build publishes nothing, so completion hooks must not run.
      if (result.success && !result.skipped) {
        this.logger.info(
          `Emitting site:build:completed event for ${environment} environment`,
        );

        await this.sendMessage({
          type: SITE_CHANNELS.buildCompleted,
          payload: {
            outputDir: data.outputDir,
            environment,
            routesBuilt: result.routesBuilt,
            siteConfig: {
              ...siteConfig,
              url: siteUrl,
            },
            generateEntityUrl: (entityType: string, slug: string) =>
              EntityUrlGenerator.getInstance().generateUrl(entityType, slug),
          },
          broadcast: true,
        });
      }

      return {
        success: result.success,
        ...(result.cancelled && { cancelled: true }),
        ...(result.skipped && { skipped: true }),
        routesBuilt: result.routesBuilt,
        outputDir: data.outputDir,
        environment,
        ...(result.errors && { errors: result.errors }),
        ...(result.warnings && { warnings: result.warnings }),
        ...(result.diagnostics && { diagnostics: result.diagnostics }),
      };
    } catch (error) {
      await this.recordStatus(
        () =>
          this.cfg.statusService?.markFailure(
            environment,
            jobId,
            getErrorMessage(error, "Site build failed"),
          ),
        "failure",
      );
      this.logger.error("Site build job failed", error);
      throw error;
    } finally {
      await this.recordLifecycle(
        () =>
          this.cfg.onBuildFinished?.(environment, jobId, inputGeneration) ??
          Promise.resolve(),
        "finished",
      );
    }
  }

  private async recordLifecycle(
    update: () => void | Promise<void>,
    state: string,
  ): Promise<void> {
    try {
      await update();
    } catch (error) {
      // The projection heals on the next read via queue reconciliation, so the
      // job must not fail here — but the lost write is an operational error.
      this.logger.error(`Failed to record site build ${state} lifecycle`, {
        error,
      });
    }
  }

  private async recordStatus(
    update: () => Promise<void> | undefined,
    state: string,
  ): Promise<void> {
    try {
      await update();
    } catch (error) {
      // Same contract as recordLifecycle: reconciliation recovers the state,
      // the build outcome stands, and the failure is loud in the logs.
      this.logger.error(`Failed to record site build ${state} state`, {
        error,
      });
    }
  }

  protected override summarizeDataForLog(
    data: SiteBuildJobData,
  ): Record<string, unknown> {
    return {
      environment: data.environment,
      outputDir: data.outputDir,
    };
  }
}
